import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "./Refine/Loading";
import QuantityControl from "../helper/QuantityControl";
import { calculateDiscountPrice, formatImageName, formatPrice, handleError } from "../utils/functions";
import axiosInstance from "../config/axios.config";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import useCurrency from "../hooks/useCurrency";
import { useTranslation } from "react-i18next";
const CartSection = () => {
  const { t } = useTranslation();
  const [currency] = useCurrency();
  const { token } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [charges, setCharges] = useState({
    estimatedShipment: 0,
    subtotal: 0,
    total: 0,
  });

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    const getData = async () => {
      try {
        setIsLoading(false);
        const { data } = await axiosInstance.get("/cart");
        if (!data.error) {
          setData(data.data.cart);
          setCharges(data.data.charges);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, [token]);

  const handleRemove = async (id) => {
    try {
      const { data } = await axiosInstance.delete(`/cart/${id}`);
      if (!data.error) {
        setData(data.data.cart);
        setCharges(data.data.charges);
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const updateQuantity = async (id, quantity) => {
    try {
      const { data } = await axiosInstance.put(`/cart/${id}`, { quantity: quantity });
      if (!data.error) {
        setData(data.data.cart);
        setCharges(data.data.charges);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const getAcceptedOfferPrice = (offers) => {
    const acceptedOffer = offers?.find((offer) => offer.status === "ACCEPTED");
    const counteredAccepted = offers.find(
      (offer) => offer.status === "COUNTERED" && offer.counterOffer.status === "ACCEPTED"
    );
    return acceptedOffer?.price || counteredAccepted.counterOffer.price || null;
  };

  return (
    <Loading isLoading={isLoading}>
      <section className="cart py-80">
        <div className="container ">
          <div className="row gy-4">
            <div className="col-md-8">
              <div className="cart-table bg-white border border-gray-100 rounded-8 px-40 py-20">
                <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                  <table className="table style-three">
                    <thead>
                      <tr>
                        <th className="h6 mb-0 px-10 py-10 cutomPrimaryColour text-lg text-white fw-bold">
                          {t("delete")}
                        </th>
                        <th className="h6 mb-0 py-10 cutomPrimaryColour text-lg text-white fw-bold">
                          {t("productName")}
                        </th>
                        <th className="h6 mb-0 py-10 cutomPrimaryColour text-lg text-white fw-bold">
                          {t("price")}
                        </th>
                        <th className="h6 mb-0 py-10 cutomPrimaryColour text-lg text-white fw-bold">
                          {t("quantity")}
                        </th>
                        <th className="h6 mb-0 px-10 py-10 cutomPrimaryColour text-lg text-white fw-bold">
                          {t("subtotal")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item) => (
                        <tr key={item._id}>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleRemove(item._id)}
                              className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                            >
                              <i className="ph ph-x-circle text-2xl d-flex" />
                              {t("remove")}
                            </button>
                          </td>
                          <td>
                            <div className="table-product d-flex align-items-center gap-10">
                              <Link
                                to={`/products/${item.product.slug}`}
                                className="table-product__thumb border border-gray-100 rounded-8 flex-center "
                              >
                                <img
                                  src={formatImageName(item.product.card)}
                                  className="max-w-100"
                                  alt={item.product.name}
                                />
                              </Link>
                              <div className="table-product__content text-start">
                                <h6 className="title text-lg fw-semibold]">
                                  <Link to="/product-details" className="link text-line-2" tabIndex={0}>
                                    {item?.product?.name?.length > 20
                                      ? `${item?.product?.name?.slice(0, 20)}...`
                                      : item?.product?.name}
                                  </Link>
                                </h6>
                                <div className="flex-align gap-16">
                                  <div className="flex-align gap-6">
                                    <span className="text-md fw-medium text-warning-600 d-flex">
                                      <i className="ph-fill ph-star" />
                                    </span>
                                    <span className="text-md fw-semibold text-gray-900">
                                      {item.product.rating}
                                    </span>
                                  </div>
                                  <span className="text-sm fw-medium text-gray-200">|</span>
                                  <span className="text-neutral-600 text-sm">
                                    {item.product.ratingCount} {t("reviews")}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="text-lg h6 mb-0 fw-semibold d-flex gap-2">
                              {getAcceptedOfferPrice(item.product.offers) ? (
                                formatPrice(getAcceptedOfferPrice(item.product.offers), currency)
                              ) : item.product.discount > 0 ? (
                                <span>
                                  {formatPrice(
                                    calculateDiscountPrice(item.product.price, item.product.discount),
                                    currency
                                  )}
                                  &nbsp;
                                  <del className="text-danger text-14">
                                    {formatPrice(item.product.price)}
                                  </del>{" "}
                                </span>
                              ) : (
                                formatPrice(item.product.price, currency)
                              )}
                            </span>
                          </td>
                          <td>
                            <QuantityControl
                              initialQuantity={item.quantity}
                              updateQuantity={updateQuantity}
                              id={item._id}
                            />
                          </td>
                          <td>
                            <span className="text-lg h6 mb-0 fw-semibold">
                              {getAcceptedOfferPrice(item.product.offers)
                                ? formatPrice(
                                    getAcceptedOfferPrice(item.product.offers) * item.quantity,
                                    currency
                                  )
                                : item.product.discount > 0
                                ? formatPrice(
                                    calculateDiscountPrice(item.product.price, item.product.discount) *
                                      item.quantity,
                                    currency
                                  )
                                : formatPrice(item.product.price * item.quantity, currency)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="cart-sidebar  bg-white border border-gray-100 rounded-8 px-24 py-40">
                <h6 className="text-xl mb-32">{t("cartTotals")}</h6>
                <div className="bg-color-three rounded-8 p-24">
                  <div className="mb-32 flex-between gap-8">
                    <span className="text-gray-900 font-heading-two">{t("subtotal")}</span>
                    <span className="text-gray-900 fw-semibold">
                      {formatPrice(charges.subtotal, currency)}
                    </span>
                  </div>
                  <div className="flex-between gap-8">
                    <span className="text-gray-900 font-heading-two">{t("estimatedDelivery")}</span>
                    <span className="text-gray-900 fw-semibold">
                      {formatPrice(charges.estimatedShipment, currency)}
                    </span>
                  </div>
                </div>
                <div className="bg-color-three rounded-8 p-24 mt-24">
                  <div className="flex-between gap-8">
                    <span className="text-gray-900 text-xl fw-semibold">{t("total")}</span>
                    <span className="text-gray-900 text-xl fw-semibold">
                      {formatPrice(charges.total, currency)}
                    </span>
                  </div>
                </div>
                <Link to="/checkout" className="btn btn-main mt-40 py-18 w-100 rounded-8">
                  {t("proceedToCheckout")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Loading>
  );
};

export default CartSection;
